import { BybHr, BybTab, BybTabBar } from "@Components/common/byb-ds";
import { DeleteMembersDialog, ProfileDetailsTab, ProfilePreferencesTab } from "@Components/game";
import { HeaderButton } from "@Components/game/header/styled-components";
import CenteredLoadingOverlay from "@Components/generic/centered-loading-overlay";
import ContainerWrapper from "@Components/generic/container-wrapper";
import HapticsButtonWrapper from "@Components/generic/haptics-button-wrapper";
import Icon from "@Components/generic/icon";
import TitleWrapper from "@Containers/shared/title-wrapper";
import { Actions, Events } from "@Context/common/general/context";
import Ownership from '@Context/common/permissions/ownership';
import Permissions from '@Context/common/permissions/permissions';
import AccountRoles from '@Enums/AccountRoles';
import VRHeaderButtonType from "@Enums/VRHeaderButtonType";
import { faTrashAlt } from "@fortawesome/pro-solid-svg-icons";
import { useApi } from '@hooks';
import useGlobalContext from "@hooks/common/general/use-global-context";
import useI18n from "@i18n";
import { Box, Container, Skeleton, Stack, useTheme } from '@mui/material';
import { Api } from "@Shared/schemas";
import { AccountUserRecord, RoleRecord } from '@Shared/schemas/accounts/accountSchemas';
import { getRoleLevel, ROLE_HIERARCHY } from "@Shared/utils/rolePermissions";
import { toNumber } from 'lodash-es';
import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';

export interface IMember {
  id: number;
  doc_id: string;
  created_at: Date;
  last_active_at: Date | null | undefined;
  profile_allowance: number | null | undefined;
  auth_id: string | null | undefined;
  email: string;
  rating: number;
  first_name: string;
  last_name: string;
  gender: string;
  country_code?: string | null;
  avatar: string | null;
  updated_at: Date | null | undefined;
  birth_date: Date | null | undefined;
  role_id: number;
}

interface IProfile {
  userId?: number;
}

const ProfileSkeleton = () => {
  const theme = useTheme();

  return (
    <Stack spacing={6}>
      <Stack direction="row" spacing={2} alignItems="center">
        <Skeleton variant="circular" width={120} height={120} />
        <Stack spacing={0.5} sx={{ textAlign: "left", flex: 1 }}>
          <Skeleton variant="text" width="60%" height={24} />
        </Stack>
      </Stack>
      <Stack spacing={1.5}>
        <Stack direction="row" spacing={1.5}>
          <Skeleton variant="rounded" width="100%" height={56} />
          <Skeleton variant="rounded" width="100%" height={56} />
        </Stack>
        <Skeleton variant="rounded" width="100%" height={56} />
      </Stack>

      <BybHr />

      <Stack spacing={1.5}>
        <Skeleton variant="rounded" width="100%" height={56} />
      </Stack>
    </Stack>
  );
};

/**
 * A container component for displaying the profile details and preferences of a general user (such as the current user) or a member (part of a team).
 *
 * @param userId
 * @returns
 */
const Profile = ({ userId }: IProfile) => {
  const { globalState, dispatch } = useGlobalContext();
  const { i18n } = useI18n();
  const theme = useTheme();
  const { memberId } = useParams();
  const formRef = useRef<HTMLFormElement>(null);
  const [selectedTab, setSelectedTab] = useState(0);
  const [memberDetails, setMemberDetails] = useState<IMember | null>(null);
  const [roles, setRoles] = useState<RoleRecord[]>([]);
  const [deleteMemberDialogOpen, setDeleteMemberDialogOpen] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [originalMemberDetails, setOriginalMemberDetails] = useState<IMember | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const userId_ = memberId ? toNumber(memberId) : userId;
  const title = !!userId ? i18n("settings.profile.title") : i18n("settings.profile.title.alt");

  // API hooks for user operations
  const { call: getUser, loading: userLoading } = useApi({
    api: Api.Users.GetUser,
    request: { user_id: userId_ }
  });

  const { call: getAccounts, loading: accountsLoading } = useApi({
    api: Api.Account.GetAccounts,
    request: {
      resolve_members: true,
      account_ids: globalState.account?.id ? [globalState.account.id] : undefined
    }
  });

  const { call: getRoles, loading: rolesLoading } = useApi({
    api: Api.Account.GetRoles,
    request: {}
  });

  const { call: updateUser } = useApi({
    api: Api.Users.UpdateUser,
    request: {} as any,
    // if editing self dispatch getAccountInfo
    onSuccess: () => {
      if(userId_ === globalState.user.dbId) {
        dispatch({
          type: Actions.UsersActions.GetAccountInfo,
          payload: {
            disableLoaders: true,
          },
        })
      };
      }
  });

  const { call: updateMember, loading: isUpdateMemberLoading } = useApi({
    api: Api.Account.UpdateMember,
    request: {} as any
  });

  const onTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setSelectedTab(newValue);
  };

  const handleFieldChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    if (!memberDetails) return;

    const updatedDetails = { ...memberDetails };
    switch (name) {
      case 'firstName':
        updatedDetails.first_name = value;
        break;
      case 'lastName':
        updatedDetails.last_name = value;
        break;
      case 'gender':
        updatedDetails.gender = value;
        break;
      case 'role':
        updatedDetails.role_id = Number(value);
        break;
      case 'countryCode':
        updatedDetails.country_code = value;
        break;
    }

    setMemberDetails(updatedDetails);
    setHasUnsavedChanges(JSON.stringify(updatedDetails) !== JSON.stringify(originalMemberDetails));
  };

  const handleDeleteMember = async () => {
    if (userId_ && globalState.account?.id && memberDetails) {
      await updateMember({
        action: 'remove',
        account_id: globalState.account.id,
        user_id: memberDetails.id,
        role_id: memberDetails.role_id
      });
      setDeleteMemberDialogOpen(false);
      Events.app.navigateBack.emit();
    }
  };

  const handleFormSubmit = async () => {
    if (!memberDetails) return;

    // Update user details
    if (userId_) {
      await updateUser({
        user_id: userId_,
        first_name: memberDetails.first_name,
        last_name: memberDetails.last_name,
        gender: memberDetails.gender,
        country_code: memberDetails.country_code || null
      });

      if (userId_ === globalState.user.dbId) {
        await new Promise<void>((resolve) => {
          dispatch({
            type: Actions.UsersActions.GetAccountInfo,
            payload: {
              disableLoaders: true,
            },
            onSuccess: () => resolve(),
            onError: () => resolve(),
          });
        });
      }

      // If this is a member (not the current user) and role has changed, update member role
      if (userId_ !== globalState.user.dbId && memberDetails.role_id && globalState.account?.id) {
        await updateMember({
          action: 'update',
          account_id: globalState.account.id,
          user_id: memberDetails.id,
          role_id: memberDetails.role_id
        });
      }

      // Refresh user data
      loadUserData();
      setHasUnsavedChanges(false);
    }
  };

  const loadUserData = async () => {
    if (!userId_ || !globalState.account?.id) return;

    setIsLoading(true);
    try {
      const [userData, accountData, rolesData] = await Promise.all([
        getUser({ user_id: userId_ }),
        getAccounts({
          resolve_members: true,
          account_ids: [globalState.account.id]
        }),
        getRoles()
      ]);

      if (rolesData?.data) {
        setRoles(rolesData.data);
      }

      if (userData && accountData?.data?.[0]) {
        const accountUser = accountData.data[0].members.find(
          (member: AccountUserRecord) => member.user_id === userData.id
        );

        const memberData = {
          ...userData,
          role_id: accountUser?.role_id || 0
        };

        setMemberDetails(memberData);
        setOriginalMemberDetails(memberData);
        setHasUnsavedChanges(false);
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Save changes before navigation
  const handleBeforeNavigate = async (event: any) => {
    if (hasUnsavedChanges) {
      await handleFormSubmit();
    }
  };

  useEffect(() => {
    Events.app.navigate.uniq(handleBeforeNavigate);
    Events.app.navigateBack.uniq(handleBeforeNavigate);

    return () => {
      Events.app.navigate.remove(handleBeforeNavigate);
      Events.app.navigateBack.remove(handleBeforeNavigate);
    };
  }, [hasUnsavedChanges, memberDetails]);

  useEffect(() => {
    Events.app.showHeaderButton.emit({ type: VRHeaderButtonType.BACK, title });
    return () => Events.app.hideHeaderButton.emit();
  }, []);

  useEffect(() => {
    if (userId_) {
      loadUserData();

      // Show delete button only if the member is not the current user
      if (userId_ !== globalState.user.dbId) {
        const isDeleteAllowed = getRoleLevel(AccountRoles[globalState.account?.user_role!]) >= ROLE_HIERARCHY[AccountRoles.ADMIN]
        Events.app.setHeaderAltSections.emit({
          rightSection: (
            <HapticsButtonWrapper>
              <HeaderButton disabled={!isDeleteAllowed} onClick={() => setDeleteMemberDialogOpen(true)}>
                <Icon icon={faTrashAlt} color={theme.palette.dsBase.v200} fontSize={24} />
              </HeaderButton>
            </HapticsButtonWrapper>
          ),
        });
      }
    }

    return () => {
      Events.app.setHeaderAltSections.emit(null);
    };
  }, [userId_]);

  return (
    <ContainerWrapper id="SETTINGS-PROFILE" sx={{ overflow: "visible" }}>
      <Ownership userId={memberDetails?.id!}>
        <Permissions ownerCanEdit={true} editor={AccountRoles.ADMIN} viewer={AccountRoles.GUEST}>
          <Container maxWidth="sm" sx={(theme) => ({ pb: theme.spacing(14) })}>
            {isUpdateMemberLoading && <CenteredLoadingOverlay loading={isUpdateMemberLoading} />}
            <TitleWrapper title={title}>
              <BybTabBar value={selectedTab} onChange={onTabChange}>
                <BybTab label={i18n("settings.profile.details")} index={0} defaultChecked size="large" />
                <BybTab label={i18n("settings.profile.preferences")} index={1} size="large" />
              </BybTabBar>

              <BybHr sx={{ mt: 3 }} />

              <form ref={formRef} onSubmit={(e) => { e.preventDefault(); handleFormSubmit(); }}>
                <Box sx={{ position: "relative", my: 5 }}>
                  {/* Tab 0: firstName, lastName, gender, country, role */}
                  <Box sx={{ position: "absolute", visibility: selectedTab === 0 ? "visible" : "hidden", width: "100%" }}>
                    {isLoading ? (
                      <ProfileSkeleton />
                    ) : (
                      <ProfileDetailsTab
                        memberDetails={memberDetails} 
                        roles={roles}
                        onChange={handleFieldChange}
                      />
                    )}
                  </Box>

                  {/* Tab 1: leaderboard, anonymous */}
                  <Box sx={{ position: "absolute", visibility: selectedTab === 1 ? "visible" : "hidden", width: "100%" }}>
                    <ProfilePreferencesTab 
                      memberPreferences={{}} 
                      isLoading={isLoading}
                    />
                  </Box>
                </Box>
              </form>
            </TitleWrapper>

            <DeleteMembersDialog
              open={deleteMemberDialogOpen}
              onDelete={handleDeleteMember}
              onCancel={() => setDeleteMemberDialogOpen(false)}
              members={memberDetails ? [memberDetails] : []}
            />
          </Container>
        </Permissions>
      </Ownership>
    </ContainerWrapper>
  );
};

export default Profile;
